import React, { useState } from 'react';
import type { SavedProfile } from '../storage/savedProfile';
import type { RedirectInfo } from '../handoff';

interface Props {
  profile: SavedProfile;
  onContinue: () => void;
  onUseDifferent: () => void;
  loading?: boolean;
  redirectInfo?: RedirectInfo | null;
}

function Spinner() {
  return (
    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
  );
}

function initialsOf(name: string | null, email: string): string {
  const source = (name && name.trim()) || email;
  const parts = source.split(/[\s.@_-]+/).filter(Boolean);
  const letters = (parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '');
  return letters.toUpperCase() || email[0]?.toUpperCase() || '?';
}

function firstNameOf(profile: SavedProfile): string {
  if (profile.displayName && profile.displayName.trim()) {
    return profile.displayName.trim().split(/\s+/)[0];
  }
  return profile.email.split('@')[0];
}

export function RememberedAccountPanel({ profile, onContinue, onUseDifferent, loading, redirectInfo }: Props) {
  const [imgFailed, setImgFailed] = useState(false);
  const showAvatar = Boolean(profile.photoURL) && !imgFailed;
  const unknownService = redirectInfo?.source === 'fallback';

  return (
    <div className="flex flex-col gap-6">
      {redirectInfo && redirectInfo.source !== 'fallback' && (
        <p className="text-center text-sm text-gray-500 dark:text-gray-400">
          Signing in to{' '}
          <span className="font-semibold text-sf-blue-dark dark:text-white">
            {redirectInfo.label}
          </span>
        </p>
      )}
      <div className="flex flex-col items-center gap-3">
        {showAvatar ? (
          <img
            src={profile.photoURL!}
            alt=""
            referrerPolicy="no-referrer"
            onError={() => setImgFailed(true)}
            className="h-12 w-12 rounded-full object-cover ring-2 ring-sf-blue-vibrant/30"
          />
        ) : (
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-sf-blue-vibrant/20 font-khand-semibold text-base text-sf-blue-vibrant">
            {initialsOf(profile.displayName, profile.email)}
          </div>
        )}
        <div className="text-center">
          {profile.displayName && (
            <p className="font-khand-semibold text-lg text-gray-800 dark:text-white">
              {profile.displayName}
            </p>
          )}
          <p className="text-sm text-gray-500 break-all dark:text-gray-400">{profile.email}</p>
        </div>
      </div>

      <button
        onClick={onContinue}
        disabled={loading || unknownService}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-sf-blue-vibrant px-6 py-3 font-medium text-white shadow-lg shadow-sf-blue-vibrant/20 transition hover:bg-sf-blue-vibrant-dark disabled:cursor-not-allowed disabled:opacity-50"
      >
        {loading && <Spinner />}
        {loading ? 'Signing in…' : `Continue as ${firstNameOf(profile)}`}
      </button>

      <button
        type="button"
        onClick={onUseDifferent}
        disabled={loading}
        className="text-center text-sm text-gray-500 transition hover:text-gray-300 disabled:opacity-40"
      >
        Use a different account
      </button>

      <p className="text-center text-xs text-gray-400">Internal use — team members only</p>
    </div>
  );
}
